import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Avatar } from './Avatar';
import { Comments } from './Comments';
import { ReactionButton } from './ReactionButton';
import { ReactionsModal } from './ReactionsModal';
import { UserProfileCard } from './UserProfileCard';
import { Post, postService, UpdatePostRequest } from '../services/postService';
import { commentService } from '../services/commentService';
import { ShareModal, ReportModal } from './social';
import { Modal } from './ui/Modal';
import { useSocial } from '../hooks/useSocial';
import { ReactionPicker, ReactionType } from './ReactionPicker';
import { LinkPreview } from './LinkPreview';
import { debugLogger } from '../utils/debugLogger';
import { BadgeDisplay } from './BadgeDisplay';
import { cn } from '../lib/utils';
import { 
  getPostAuthorName, 
  getReactionCount, 
  getCommentCount, 
  getShareCount, 
  getPostAuthorForAvatar
} from '../utils/postUtils';
import { usePostWebSocket } from '../hooks/usePostWebSocket';

type VisibilityOption = 'public_' | 'friends' | 'private_' | 'group';

interface PostCardProps {
  post: Post;
  onReaction: (id: number, reaction: ReactionType) => void;
  onUnreact: (id: number) => void;
  onComment: (id: number, content: string) => Promise<void>;
  onShare: (id: number) => void;
  onDelete?: (id: number) => void;
  currentUser?: any;
  currentUserId?: number; // Added currentUserId prop
  className?: string;
  highlight?: string;
  onPostUpdated?: (post: Post) => void;
  enableEditing?: boolean;
}

export const PostCard: React.FC<PostCardProps> = ({ 
  post, 
  onReaction, 
  onUnreact, 
  onComment, 
  onShare, 
  onDelete,
  currentUser,
  currentUserId,
  className = '',
  highlight,
  onPostUpdated,
  enableEditing = false
}) => {
  const { isAuthenticated } = useAuth();
  
  const moodTextMap: Record<string, string> = {
    Anger: 'Giận dữ',
    Disgust: 'Ghê tởm',
    Fear: 'Sợ hãi',
    Sadness: 'Buồn bã',
    Enjoyment: 'Vui vẻ',
    Other: 'Khác',
    Surprise: 'Ngạc nhiên'
  };
  
  const getMoodText = (mood: string | undefined): string => {
    if (!mood) return '';
    return moodTextMap[mood] || mood;
  };
  
  const topicTextMap: Record<string, string> = {
    chinh_tri_xa_hoi: 'Chính trị - Xã hội',
    doi_song: 'Đời sống',
    khoa_hoc: 'Khoa học',
    kinh_doanh: 'Kinh doanh',
    phap_luat: 'Pháp luật',
    suc_khoe: 'Sức khỏe',
    the_gioi: 'Thế giới',
    the_thao: 'Thể thao',
    van_hoa: 'Văn hóa',
    vi_tinh: 'Vi tính'
  };
  
  const topicColorMap: Record<string, { bg: string; text: string; border: string }> = {
    chinh_tri_xa_hoi: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    doi_song: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    khoa_hoc: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    kinh_doanh: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    phap_luat: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300' },
    suc_khoe: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
    the_gioi: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
    the_thao: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    van_hoa: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
    vi_tinh: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' }
  };
  
  const getTopicText = (topic: string | undefined): string => {
    if (!topic) return '';
    return topicTextMap[topic] || topic;
  };
  
  const getTopicColors = (topic: string | undefined) => {
    if (!topic) return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
    return topicColorMap[topic] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
  };
  
  const classificationTextMap: Record<string, string> = {
    other: 'Khác',
    sex: 'Nội dung nhạy cảm',
    violence: 'Bạo lực'
  };
  
  const getClassificationText = (label: string | undefined): string => {
    if (!label) return '';
    return classificationTextMap[label] || label;
  };

  // Helper to convert authorBadges to Badge[] format
  const convertAuthorBadgesToBadges = (authorBadges?: Array<{ badgeId: number; badgeType: string; badgeName: string; badgeIconUrl: string }>) => {
    if (!authorBadges) return [];
    return authorBadges.map(badge => ({
      id: badge.badgeId,
      type: badge.badgeType as any,
      name: badge.badgeName,
      iconUrl: badge.badgeIconUrl,
      isAutoAssignable: false,
      createdAt: '',
      updatedAt: ''
    }));
  };
  const normalize = (text: string) => (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const renderHighlighted = (content: string, query?: string) => {
    if (!query) return <>{content}</>;
    const normContent = normalize(content);
    const normQuery = normalize(query.trim());
    if (!normQuery) return <>{content}</>;
    const parts: React.ReactNode[] = [];
    let start = 0;
    let index = normContent.indexOf(normQuery, start);
    while (index !== -1) {
      if (index > start) {
        parts.push(<span key={`t-${start}`}>{content.slice(start, index)}</span>);
      }
      parts.push(
        <mark key={`m-${index}`} className="bg-yellow-200 px-0.5 rounded">
          {content.slice(index, index + normQuery.length)}
        </mark>
      );
      start = index + normQuery.length;
      index = normContent.indexOf(normQuery, start);
    }
    parts.push(<span key={`t-end`}>{content.slice(start)}</span>);
    return <>{parts}</>;
  };

  const extractFirstUrl = (text: string): string | null => {
    const match = text.match(/https?:\/\/[^\s]+/i);
    return match ? match[0] : null;
  };
  const normalizeVisibility = (value?: string): VisibilityOption => {
    if (value === 'friends') return 'friends';
    if (value === 'private' || value === 'private_') return 'private_';
    return 'public_';
  };
  const [postContent, setPostContent] = useState(post.content || '');
  const [postVisibility, setPostVisibility] = useState<string>(post.visibility);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [editVisibility, setEditVisibility] = useState<VisibilityOption>(normalizeVisibility(post.visibility));
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { sharePost, reportPost } = useSocial();
  const toast = useToast();
  const [showComments, setShowComments] = useState(false);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [previewComments, setPreviewComments] = useState<any[]>([]);
  const [isLoadingPreviewComments, setIsLoadingPreviewComments] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [userProfilePosition, setUserProfilePosition] = useState({ x: 0, y: 0 });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedContent, setExpandedContent] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLParagraphElement | null>(null);
  const userAvatarRef = useRef<HTMLDivElement>(null);

  // Realtime WebSocket updates
  const { post: wsPost, setPost: setWsPost, latestUpdate } = usePostWebSocket(post.id);
  const [reactionCount, setReactionCount] = useState(post.reactions?.totalCount || post.reactionCount || 0);
  const [commentCount, setCommentCount] = useState(post.commentCount || 0);
  const [shareCount, setShareCount] = useState(post.shareCount || 0);

  // Initialize WebSocket post state
  useEffect(() => {
    if (post) {
      setWsPost(post);
      setReactionCount(post.reactions?.totalCount || post.reactionCount || 0);
      setCommentCount(post.commentCount || 0);
      setShareCount(post.shareCount || 0);
    }
  }, [post.id, post.reactions?.totalCount, post.reactionCount, post.commentCount, post.shareCount, setWsPost]);

  // Sync WebSocket updates to local state with animation
  useEffect(() => {
    if (wsPost && latestUpdate) {
      console.log('🔔 PostCard WebSocket Update:', {
        postId: post.id,
        type: latestUpdate.type,
        data: latestUpdate.data,
        wsPost: {
          reactionCount: wsPost.reactionCount,
          commentCount: wsPost.commentCount,
          shareCount: wsPost.shareCount,
          reactions: wsPost.reactions
        },
        currentState: { reactionCount, commentCount, shareCount }
      });
      
      // Update counts with animation - WebSocket has priority
      const newReactionCount = wsPost.reactions?.totalCount || wsPost.reactionCount || 0;
      if (newReactionCount !== reactionCount) {
        console.log('📈 Updating reaction count:', reactionCount, '→', newReactionCount);
        setReactionCount(newReactionCount);
      }
      
      const newCommentCount = wsPost.commentCount || 0;
      if (newCommentCount !== commentCount) {
        console.log('💬 Updating comment count:', commentCount, '→', newCommentCount);
        setCommentCount(newCommentCount);
        // Reload comments if viewing
        if (showComments) {
          loadPreviewCommentsData();
        }
      }
      
      const newShareCount = wsPost.shareCount || 0;
      if (newShareCount !== shareCount) {
        console.log('🔄 Updating share count:', shareCount, '→', newShareCount);
        setShareCount(newShareCount);
      }
    }
  }, [wsPost, latestUpdate]);

  // Load preview comments helper
  const loadPreviewCommentsData = async () => {
    try {
      setIsLoadingPreviewComments(true);
      const response = await commentService.getCommentsByPost(post.id, 0, 3);
      setPreviewComments(response.content || []);
    } catch (error) {
      console.error('Failed to load preview comments:', error);
    } finally {
      setIsLoadingPreviewComments(false);
    }
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (!contentRef.current) return;
      const el = contentRef.current;
      const isOverflowing = el.scrollHeight > el.clientHeight;
      setHasOverflow(isOverflowing);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [postContent]);

  useEffect(() => {
    setPostContent(post.content || '');
  }, [post.id, post.content]);

  useEffect(() => {
    setPostVisibility(post.visibility);
  }, [post.id, post.visibility]);

  const actualCommentCount = post.commentCount ?? 0;

  useEffect(() => {
    const loadPreviewComments = async () => {
      if (!showComments) return;

      try {
        setIsLoadingPreviewComments(true);
        
        const response = await commentService.getCommentsByPost(post.id, 0, 3);
        setPreviewComments(response.content || []);
        
        debugLogger.log('PostCard', 'Loaded preview comments', { 
          postId: post.id, 
          previewCommentsCount: response.content?.length || 0
        });
      } catch (error) {
        debugLogger.log('PostCard', 'Failed to load preview comments', { 
          postId: post.id, 
          error 
        });
        setPreviewComments([]);
      } finally {
        setIsLoadingPreviewComments(false);
      }
    };

    loadPreviewComments();
  }, [showComments, post.id]);

  const handleUserHover = (e: React.MouseEvent) => {
    if (userAvatarRef.current) {
      const rect = userAvatarRef.current.getBoundingClientRect();
      setUserProfilePosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setShowUserProfile(true);
    }
  };

  const handleUserLeave = () => {
    setShowUserProfile(false);
  };

  const handleUserClick = () => {
    navigate(`/profile/${post.authorId}`);
  };

  const handleShare = async (message?: string) => {
    try {
      await sharePost(post.id, message);
      onShare(post.id);
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleReport = async (reason: string, description: string) => {
    try {
      await reportPost(post.id, reason, description);
    } catch (error) {
      console.error('Report error:', error);
    }
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setShowImageViewer(true);
  };

  const handleNextImage = () => {
    if (post.media && currentImageIndex < post.media.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const loadPostReactions = async (postId: number, page: number = 0) => {
    const response = await postService.getPostReactions(postId, page, 10);
    return response.content || [];
  };

  const AnimatedIcon = ({ children }: { children: React.ReactNode }) => (
    <div className="transition-transform duration-300 hover:scale-110">{children}</div>
  );

  const [activeReactionPicker, setActiveReactionPicker] = useState<number | null>(null);
  const [pickerPos, setPickerPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const canReact = isAuthenticated;
  
  const OFFSET_Y = 40;     
  const OFFSET_X = 10;        
  const PICKER_W = 280;     
  const PICKER_H = 56;      
  
  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));
  
  const handleReactionEnter = (e: React.MouseEvent, postId: number) => {
    if (!canReact) return;
    if (hoverTimeout) clearTimeout(hoverTimeout);
    console.log('Hovering over ReactionButton, postId:', postId); // Log hover event
    const timeout = setTimeout(() => {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height;
      console.log('Showing ReactionPicker at position:', { x, y }); // Log picker position
      setPickerPos({ x, y });
      setActiveReactionPicker(postId);
      console.log('ReactionPicker state set to active for postId:', postId); // Log state change
    }, 500); // Show picker after 500ms
    setHoverTimeout(timeout);
  };
  
  const handleReactionLeave = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    console.log('Hover leave detected, but ReactionPicker will remain open'); // Log hover leave
  }; // Prevent hiding on mouse leave

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        reactionPickerRef.current &&
        !reactionPickerRef.current.contains(event.target as Node)
      ) {
        console.log('Closing ReactionPicker: Clicked outside'); // Log reason for closing
        setActiveReactionPicker(null);
      }
    };

    if (activeReactionPicker !== null) {
      console.log('Adding click outside listener for ReactionPicker'); // Log listener addition
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        console.log('Removing click outside listener for ReactionPicker'); // Log listener removal
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeReactionPicker]);
  
  const handleReactionMove = (e: React.MouseEvent) => {
    if (!canReact) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
  
    let x = e.clientX;
    let y = e.clientY - 200000;
  
    // Clamp to prevent overflow
    x = clamp(x, 8, vw - PICKER_W - 8);
    y = clamp(y, 8, vh - PICKER_H - 8);
  
    setPickerPos({ x, y });
  };
  
  useEffect(() => {
    const onResize = () => setActiveReactionPicker(null);
    const onScroll = () => {
      if (activeReactionPicker) {
        setActiveReactionPicker(null);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  
  const handleReaction = (reaction: ReactionType) => {
    if (!canReact) return;
    if (post.reactions) {
        const currentReactionType = post.reactions.currentUserReactionType;
        const reactionCounts = post.reactions.reactionCounts;

        if (currentReactionType === reaction) {
            // User is unreacting
            const reactionIndex = reactionCounts.findIndex(r => r.type === reaction);
            if (reactionIndex !== -1) {
                reactionCounts[reactionIndex].count -= 1;
                if (reactionCounts[reactionIndex].count === 0) {
                    reactionCounts.splice(reactionIndex, 1);
                }
            }
            post.reactions.currentUserReacted = false;
            post.reactions.currentUserReactionType = null;
        } else {
            // User is reacting
            if (currentReactionType) {
                // Remove previous reaction
                const previousReactionIndex = reactionCounts.findIndex(r => r.type === currentReactionType);
                if (previousReactionIndex !== -1) {
                    reactionCounts[previousReactionIndex].count -= 1;
                    if (reactionCounts[previousReactionIndex].count === 0) {
                        reactionCounts.splice(previousReactionIndex, 1);
                    }
                }
            }

            const newReactionIndex = reactionCounts.findIndex(r => r.type === reaction);
            if (newReactionIndex !== -1) {
                reactionCounts[newReactionIndex].count += 1;
            } else {
                reactionCounts.push({ type: reaction, count: 1 });
            }
            post.reactions.currentUserReacted = true;
            post.reactions.currentUserReactionType = reaction;
        }

        post.reactions.totalCount = reactionCounts.reduce((sum, r) => sum + r.count, 0);
    }

    onReaction(post.id, reaction);
    setShowReactionsModal(false); // Close the picker after reacting
  };

  const getReactionCount = (post: Post) => {
      if (!post.reactions) return 0;
      return post.reactions.totalCount || 0; // Use totalCount for accurate reaction count
  };

  const isCurrentUserPost = post.authorId === currentUserId; // Check if the post belongs to the current user
  const canEditPost = enableEditing && isCurrentUserPost;
  const createFailed = (post as any).createFailed === true;

  const handleDelete = async () => {
    try {
      if (onDelete) {
        await postService.deletePost(post.id);
        onDelete(post.id);
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleOpenEditModal = () => {
    setEditContent(postContent);
    setEditVisibility(normalizeVisibility(postVisibility));
    setUpdateError(null);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setUpdateError(null);
  };

  const handleUpdatePost = async () => {
    if (!editContent.trim()) {
      setUpdateError('Nội dung không được để trống');
      return;
    }

    setIsUpdatingPost(true);
    setUpdateError(null);

    const payload: UpdatePostRequest = {
      content: editContent.trim(),
      visibility: editVisibility,
    };

    const existingMediaUrls =
      post.media?.map((mediaItem) => mediaItem.mediaUrl) ??
      post.images ??
      undefined;

    if (existingMediaUrls && existingMediaUrls.length > 0) {
      payload.mediaUrls = existingMediaUrls;
    }

    try {
      const updatedPost = await postService.updatePost(post.id, payload);
      setPostContent(updatedPost.content || '');
      setPostVisibility(updatedPost.visibility);
      onPostUpdated?.(updatedPost);
      setShowEditModal(false);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Cập nhật bài viết thất bại. Vui lòng thử lại.';
      setUpdateError(message);
    } finally {
      setIsUpdatingPost(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 mb-6 max-w-2xl mx-auto ${className}`}>
      <div className="p-5 flex items-center space-x-3">
        <div
          ref={userAvatarRef}
          onMouseEnter={handleUserHover}
          onMouseLeave={handleUserLeave}
          onClick={handleUserClick}
          className="cursor-pointer"
        >
          <Avatar 
            user={getPostAuthorForAvatar(post)}
            size="lg"
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 flex-wrap">
            <h3 
              className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={handleUserClick}
            >
              {getPostAuthorName(post)}
              {createFailed && (
                <span className="ml-2 inline-block text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Lỗi đăng</span>
              )}
            </h3>
            {(post.authorBadges || post.author?.badges) && (
              <BadgeDisplay 
                badges={post.authorBadges ? convertAuthorBadgesToBadges(post.authorBadges) : (post.author?.badges || [])} 
                size="sm" 
                maxDisplay={5} 
              />
            )}
          </div>
          {/* Group info if post is from a group */}
          {post.groupId && post.groupName && (
            <div className="flex items-center space-x-1.5 text-sm mb-1">
              <svg className="w-4 h-4 text-cyan-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span className="text-gray-600">đăng trong</span>
              <span className="font-semibold text-cyan-600 hover:text-cyan-700 hover:underline cursor-pointer transition-colors" onClick={() => navigate(`/groups/${post.groupId}`)}>
                {post.groupName}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-500">
              @{post.authorUsername || post.author?.username || `user${post.authorId}`} · {new Date(post.createdAt).toLocaleString('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            {/* Visibility indicator */}
            <div className="flex items-center space-x-1">
              {/* Debug: Log visibility value */}
              {(() => {
                console.log('PostCard visibility:', postVisibility, 'for post:', post.id);
                return null;
              })()}
              {postVisibility === 'public_' && (
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                  <span>Công khai</span>
                </div>
              )}
              {postVisibility === 'friends' && (
                <div className="flex items-center space-x-1 text-xs text-blue-500">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  <span>Bạn bè</span>
                </div>
              )}
              {(postVisibility === 'private' || postVisibility === 'private_') && (
                <div className="flex items-center space-x-1 text-xs text-orange-500">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>Riêng tư</span>
                </div>
              )}
              {postVisibility === 'group' && (
                <div className="flex items-center space-x-1 text-xs text-cyan-500">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  <span>Nhóm</span>
                </div>
              )}
              {/* Fallback for unknown visibility values */}
              {postVisibility && !['public_', 'friends', 'private', 'private_', 'group'].includes(postVisibility) && (
                <div className="flex items-center space-x-1 text-xs text-gray-400">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>{postVisibility}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Enhance header icons for better visibility and aesthetics */}
        <div className="flex items-center space-x-1">
          {isCurrentUserPost ? (
            <>
              {canEditPost && (
                <button
                  onClick={handleOpenEditModal}
                  className="p-2 hover:bg-blue-50 rounded-full transition-all duration-300"
                  title="Cập nhật bài viết"
                >
                  <svg className="w-6 h-6 text-blue-500 hover:text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4h2m3.586 0a1 1 0 01.707.293l2.414 2.414a1 1 0 010 1.414l-9.9 9.9a1 1 0 01-.45.263l-4 1a1 1 0 01-1.213-1.213l1-4a1 1 0 01.263-.45l9.9-9.9A1 1 0 0116.586 4z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5l4 4" />
                  </svg>
                </button>
              )}
              <button 
                onClick={async () => {
                  const confirmed = await toast.confirm(
                    'Xóa bài viết?',
                    'Bài viết sẽ bị xóa vĩnh viễn và không thể khôi phục.',
                    {
                      confirmText: 'Xóa',
                      cancelText: 'Hủy',
                      type: 'danger',
                      icon: 'delete'
                    }
                  );

                  if (confirmed) {
                    try {
                      await postService.deletePost(post.id);
                      onDelete?.(post.id);
                      toast.success('Xóa bài viết thành công');
                    } catch (error) {
                      console.error('Failed to delete post:', error);
                      toast.error('Xóa bài viết thất bại', 'Vui lòng thử lại.');
                    }
                  }
                }}
                className="p-2 hover:bg-red-100 rounded-full group transition-all duration-300"
                title="Xóa bài viết"
              >
                <svg className="w-6 h-6 text-red-500 group-hover:text-red-700" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </>
          ) : (
            <button 
              onClick={() => setShowReportModal(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-300"
              title="Báo cáo bài viết"
            >
              <svg className="w-6 h-6 text-gray-500 hover:text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Share Info Display - Show who shared this post */}
      {post.shareInfo && (
        <div className="mx-5 mb-4 mt-3 rounded-xl bg-gradient-to-br from-blue-50/80 to-indigo-50/60 border border-blue-100/50 backdrop-blur-sm">
          <div className="p-4">
            <div className="flex items-start space-x-3">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <img
                    src={post.shareInfo.sharedByAvatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.shareInfo.sharedByDisplayName)}&background=3b82f6&color=fff`}
                    alt={post.shareInfo.sharedByDisplayName}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                  />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="font-semibold text-gray-900 hover:text-blue-600 cursor-pointer transition-colors">
                    {post.shareInfo.sharedByDisplayName}
                  </span>
                  
                  {/* Badges */}
                  {post.shareInfo.sharedByBadges && post.shareInfo.sharedByBadges.length > 0 && (
                    <div className="flex items-center gap-1">
                      {post.shareInfo.sharedByBadges.slice(0, 3).map((badge, index) => (
                        <div
                          key={index}
                          className="relative group"
                          title={badge.name}
                        >
                          <img
                            src={badge.iconUrl}
                            alt={badge.name}
                            className="w-5 h-5 object-contain drop-shadow-sm hover:scale-110 transition-transform"
                          />
                        </div>
                      ))}
                      {post.shareInfo.sharedByBadges.length > 3 && (
                        <span className="text-xs text-gray-500 font-medium">
                          +{post.shareInfo.sharedByBadges.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <span className="text-sm text-gray-600">đã chia sẻ</span>
                </div>
                
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{new Date(post.shareInfo.sharedAt).toLocaleDateString('vi-VN', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>

                {/* Share Comment */}
                {post.shareInfo.shareComment && (
                  <div className="mt-3 p-3 bg-white/60 backdrop-blur-sm rounded-lg border border-blue-100/30 shadow-sm">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {post.shareInfo.shareComment}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 pb-4">
        {/* Mood and Topic display */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {post.mood && (
            <div className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-full border border-blue-200">
              <span className="text-xs font-medium text-blue-700">
                Tâm trạng: {getMoodText(post.mood)}
              </span>
              {typeof post.moodScore === 'number' && (
                <span className="ml-1 text-xs text-blue-500">
                  ({(post.moodScore * 100).toFixed(0)}%)
                </span>
              )}
            </div>
          )}
          
          {post.topic && (
            <div className={`inline-flex items-center px-2.5 py-1 rounded-full border ${getTopicColors(post.topic).bg} ${getTopicColors(post.topic).border}`}>
              <svg className={`w-3 h-3 mr-1.5 ${getTopicColors(post.topic).text}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className={`text-xs font-medium ${getTopicColors(post.topic).text}`}>
                {getTopicText(post.topic)}
              </span>
              {typeof post.topicConfidence === 'number' && (
                <span className={`ml-1 text-xs opacity-70 ${getTopicColors(post.topic).text}`}>
                  ({(post.topicConfidence * 100).toFixed(0)}%)
                </span>
              )}
            </div>
          )}
        </div>
        
        {postContent && (
          <>
            <p
              ref={(el) => { contentRef.current = el; }}
              className={`text-gray-900 leading-relaxed break-words whitespace-pre-wrap ${expandedContent ? '' : 'line-clamp-3'}`}
            >
              {renderHighlighted(postContent, highlight)}
            </p>
            {hasOverflow && (
              <div className="mt-1">
                {!expandedContent ? (
                  <button onClick={() => setExpandedContent(true)} className="text-sm text-blue-600 mt-1 cursor-pointer select-none">Xem thêm</button>
                ) : (
                  <button onClick={() => setExpandedContent(false)} className="text-sm text-blue-600 mt-1 cursor-pointer select-none">Thu gọn</button>
                )}
              </div>
            )}
          </>
        )}
        {extractFirstUrl(postContent) && (
          <div className="mt-3">
            <LinkPreview url={extractFirstUrl(postContent)!} />
          </div>
        )}
      </div>

      {post.images && post.images.length > 0 && (
        <div className="px-5 pb-4">
          <div className={`grid gap-2 ${post.images && post.images.length === 1 ? 'grid-cols-1' : post.images && post.images.length === 2 ? 'grid-cols-2' : post.images && post.images.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {post.images?.slice(0, 4).map((image, index) => (
              <div key={index} className={`${post.images && post.images.length === 3 && index === 0 ? 'col-span-2' : ''} overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center`}>
                <img
                  src={image}
                  alt={`Post content ${index + 1}`}
                  className="w-full h-auto max-h-96 object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
                  onClick={() => window.open(image, '_blank')}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {post.media && post.media.length > 0 && (
        <div className="px-5 pb-4">
          <div className={`grid gap-2 ${
            post.media.length === 1 ? 'grid-cols-1' : 
            post.media.length === 2 ? 'grid-cols-2' : 
            post.media.length === 3 ? 'grid-cols-3' : 
            post.media.length === 4 ? 'grid-cols-2' :
            'grid-cols-3'
          }`}>
            {post.media?.slice(0, 5).map((mediaItem, index) => {
              // Don't render if image failed to load
              if (imageErrors.has(index)) return null;
              
              return (
                <div key={index} className={`relative overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center ${
                  post.media && post.media.length === 5 && index >= 3 ? 'col-span-1' : 
                  post.media && post.media.length > 5 && index === 0 ? 'col-span-2 row-span-2' :
                  ''
                }`}>
                  {/* Classification label display - Only show label, no percentage */}
                  {mediaItem.classificationLabel && mediaItem.classificationLabel !== 'other' && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className={cn(
                        "text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm",
                        mediaItem.classificationLabel === 'sex' 
                          ? "bg-gradient-to-r from-orange-500/90 to-orange-600/90"
                          : mediaItem.classificationLabel === 'violence'
                          ? "bg-gradient-to-r from-red-500/90 to-red-600/90"
                          : "bg-black/70"
                      )}>
                        {getClassificationText(mediaItem.classificationLabel)}
                      </div>
                    </div>
                  )}
                  {mediaItem.mediaType === 'video' ? (
                    <video
                      src={mediaItem.mediaUrl}
                      controls
                      className="w-full h-auto max-h-96 object-contain"
                      preload="metadata"
                    >
                      Trình duyệt của bạn không hỗ trợ video.
                    </video>
                  ) : (
                    <img
                      src={mediaItem.mediaUrl}
                      alt={`Post media ${index + 1}`}
                      className="w-full h-auto max-h-96 object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
                      onClick={() => handleImageClick(index)}
                      onError={() => handleImageError(index)}
                      loading="lazy"
                    />
                  )}
                  {index === 4 && post.media && post.media.length > 5 && (
                    <div 
                      className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center cursor-pointer z-10"
                      onClick={() => handleImageClick(index)}
                    >
                      <span className="text-white text-3xl font-bold">+{post.media.length - 5}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-5 py-3 flex items-center justify-between text-sm text-gray-500 border-b border-gray-100">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (reactionCount > 0) {
                debugLogger.logButtonClick('Reaction Count Click', { postId: post.id, count: reactionCount });
                setShowReactionsModal(true);
              }
            }}
            className={`hover:text-blue-600 transition-colors ${reactionCount > 0 ? 'cursor-pointer hover:underline' : 'cursor-default'} ${latestUpdate?.type === 'REACTION_ADDED' ? 'animate-pulse' : ''}`}
          disabled={reactionCount === 0}
        >
          {reactionCount > 0 ? `${reactionCount} lượt thích` : 'Chưa có lượt thích'}
        </button>
        <span className={latestUpdate?.type === 'COMMENT_ADDED' ? 'animate-pulse text-blue-600 font-semibold' : ''}>
          {commentCount > 0 ? `${commentCount} bình luận` : 'Không có bình luận nào'}
        </span>
      </div>
      <span className={latestUpdate?.type === 'SHARE_ADDED' ? 'animate-pulse text-green-600 font-semibold' : ''}>
        {shareCount > 0 ? `${shareCount} lượt chia sẻ` : 'Chưa có chia sẻ'}
      </span>
    </div>
      <div className="px-5 py-4 flex items-center justify-between">
        <div
          className="relative"
          onMouseEnter={(e) => handleReactionEnter(e, post.id)}
          onMouseMove={handleReactionMove}
        >
          <ReactionButton
            postId={post.id} // Added postId prop
            currentReaction={post.userReaction as ReactionType}
            onReaction={(reaction) => onReaction(post.id, reaction)}
            onUnreact={() => onUnreact(post.id)}
            reactionCount={reactionCount}
            reactions={post.reactions}
            disabled={!canReact}
          />
        
        </div>
        
        <button
          onClick={() => {
            debugLogger.logButtonClick('Toggle Comments', { postId: post.id, currentlyVisible: showComments });
            setShowComments(!showComments);
          }}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors ${
            showComments ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className={showComments ? 'font-semibold' : ''}>
            {showComments ? 'Ẩn bình luận' : 'Bình luận'}
          </span>
        </button>

        <button
          onClick={() => {
            debugLogger.logButtonClick('Share Post', { postId: post.id });
            setShowShareModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
          </svg>
          <span>Chia sẻ</span>
        </button>
      </div>

      {/* Handle case when there are no comments */}
      {!showComments && actualCommentCount !== null && (
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
          {isLoadingPreviewComments ? (
            <div className="text-center text-gray-500 text-sm py-2">
              <div className="animate-pulse">Đang tải bình luận...</div>
            </div>
          ) : actualCommentCount > 0 ? (
            <div className="space-y-3">
              {previewComments.slice(0, 3).map((comment) => (
                <div key={comment.id} className="flex items-start space-x-3">
                  <Avatar 
                    user={comment.author}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">
                        {comment.author?.displayName || comment.author?.username || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.createdAt).toLocaleDateString('vi-VN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))}
              {actualCommentCount > 3 && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowComments(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                  >
                    Xem tất cả {actualCommentCount} bình luận
                  </button>
                </div>
              )}
              {actualCommentCount <= 3 && actualCommentCount > 0 && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowComments(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                  >
                    Xem bình luận
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 text-sm py-2">
              Chưa có bình luận nào, hãy là người đầu tiên bình luận.
            </div>
          )}
        </div>
      )}

      {/* Comments Section */}
      <Comments
        postId={post.id}
        isVisible={showComments}
        initialComments={previewComments}
        onCommentAdded={() => {
          // Comment count will be updated from parent component
        }}
        onCommentCountUpdate={(count) => {
          // Comment count is managed by the post data from API
          // Parent component should refresh the post data if needed
        }}
      />      {/* Reactions Modal */}
      <ReactionsModal
        isVisible={showReactionsModal}
        onClose={() => setShowReactionsModal(false)}
        postId={post.id}
        reactions={post.reactions}
        onLoadReactions={loadPostReactions}
      />

      {/* User Profile Card */}
      <UserProfileCard
        user={{
          id: post.authorId,
          username: post.authorUsername || post.author?.username || `user${post.authorId}`,
          displayName: getPostAuthorName(post),
          avatarUrl: post.authorAvatarUrl || post.author?.avatarUrl || undefined
        }}
        isVisible={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        position={userProfilePosition}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        postId={post.id}
        postContent={postContent}
        postAuthor={getPostAuthorName(post)}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onReport={handleReport}
        targetType="POST"
        targetId={post.id}
        targetName={getPostAuthorName(post)}
      />

      <Modal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title="Cập nhật bài viết"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nội dung
            </label>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[120px] resize-none"
              placeholder="Chia sẻ điều bạn đang nghĩ..."
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {editContent.length}/10000
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quyền riêng tư
            </label>
            <select
              value={editVisibility}
              onChange={(e) => setEditVisibility(e.target.value as VisibilityOption)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="public_">🌍 Công khai</option>
              <option value="friends">👥 Bạn bè</option>
              <option value="private_">🔒 Chỉ mình tôi</option>
            </select>
          </div>
          {updateError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-600">
              {updateError}
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleCloseEditModal}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isUpdatingPost}
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleUpdatePost}
              disabled={isUpdatingPost || !editContent.trim()}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingPost ? 'Đang lưu...' : 'Cập nhật'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Image Viewer Modal */}
      {showImageViewer && post.media && post.media.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={() => setShowImageViewer(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowImageViewer(false);
            }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous Button */}
          {currentImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousImage();
              }}
              className="absolute left-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image */}
          <div className="max-w-7xl max-h-screen p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={post.media[currentImageIndex]?.mediaUrl}
              alt={`Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="text-center text-white mt-4">
              <p className="text-sm">
                {currentImageIndex + 1} / {post.media.filter((_, idx) => !imageErrors.has(idx)).length}
              </p>
            </div>
          </div>

          {/* Next Button */}
          {currentImageIndex < post.media.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

